import React, { useState, useEffect } from 'react';
import { Card, List, Tag, Button, Typography, Space, Divider, Tabs, message, Grid, Spin, Modal, Table } from 'antd';
import {
  EnvironmentOutlined, PhoneOutlined, ShoppingCartOutlined, CheckCircleOutlined, DollarOutlined,
  CarOutlined, EyeOutlined, ShoppingOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import LayoutHome from '../../components/layout/LayoutHome';
import { useAuth } from '../../aws/AuthContext';
import axios from 'axios';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const API_BASE_URL = process.env.REACT_APP_API_URL;

const MiRuta = () => {
  const screens = useBreakpoint();
  const isMobile = screens.md === false;
  const navigate = useNavigate();
  const { getJwtToken } = useAuth();

  const [ordenes, setOrdenes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesandoId, setProcesandoId] = useState(null);

  // Estados para el Modal de Ver Detalle
  const [modalVerOpen, setModalVerOpen] = useState(false);
  const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);
  const [detallesOrden, setDetallesOrden] = useState([]);
  const [loadingDetalles, setLoadingDetalles] = useState(false);

  // Cargar las órdenes asignadas al repartidor
  const fetchMiRuta = async () => {
    setLoading(true);
    try {
      const token = await getJwtToken();
      // El backend filtra las órdenes por el repartidor logueado o asignadas a la ruta
      const res = await axios.get(`${API_BASE_URL}/v1-ordenes-venta?mi_ruta=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrdenes(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      message.error("No se pudieron cargar tus pedidos asignados.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMiRuta();
  }, []);

  // Filtrar órdenes por estado
  const rutaHoy = ordenes.filter(o => o.estado === 'en_ruta');
  const historial = ordenes.filter(o => o.estado === 'completado' || o.estado === 'cancelado');

  // Acción principal: Cargar el detalle de la orden y enviarlo al POS de Ventas
  const handleEntregarYCobrar = async (orden) => {
    setProcesandoId(orden.id);
    try {
      const token = await getJwtToken();

      // 1. Descargar los productos específicos de esta orden
      const resDetalles = await axios.get(`${API_BASE_URL}/v1-ordenes-venta?orden_id=${orden.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const detalles = Array.isArray(resDetalles.data) ? resDetalles.data : [];
      if (detalles.length === 0) {
        message.warning("Este pedido no tiene productos registrados.");
        setProcesandoId(null);
        return;
      }

      // 2. Mapear los productos al formato exacto que usa el carrito de Ventas.jsx
      const carritoParaCobro = detalles.map(d => ({
        id: d.variante_id ? `${d.producto_id}_${d.variante_id}` : d.producto_id,
        producto_id: d.producto_id,
        variante_id: d.variante_id,
        nombre: d.producto_nombre || d.nombre,
        precioVenta: Number(d.precio_unitario),
        precioPorDefecto: Number(d.precio_unitario),
        numItems: Number(d.cantidad),
        descuento: Number(d.descuento || 0),
        stock: 999, // Stock asumido porque la mercancía ya va física en el camión
        precio_editado: false
      }));

      // 3. Navegar a /ventas enviando el estado de la orden por Router state
      navigate('/ventas', {
        state: {
          esEntregaPedido: true,
          ordenId: orden.id,
          cliente: {
            id: orden.cliente_id,
            nombre: orden.cliente_nombre,
            nombre_comercial: orden.cliente_comercial,
            telefono: orden.cliente_telefono,
            direccion: orden.cliente_direccion
          },
          zona: { id: orden.zona_id, nombre: orden.zona_nombre },
          carrito: carritoParaCobro
        }
      });

    } catch (error) {
      console.error(error);
      message.error("Error al preparar la orden para cobrar.");
      setProcesandoId(null);
    }
  };

  // UI de la Tarjeta del Pedido
  const renderOrdenCard = (orden, esHistorial = false) => {
    const isCompletado = orden.estado === 'completado';
    const isCancelado = orden.estado === 'cancelado';

    let borderColor = '#3b82f6'; // Azul por defecto (en ruta)
    if (isCompletado) borderColor = '#10b981'; // Verde
    if (isCancelado) borderColor = '#ef4444'; // Rojo

    return (
      <Card
        key={orden.id}
        style={{
          marginBottom: '12px',
          borderRadius: '12px',
          borderLeft: `5px solid ${borderColor}`,
          boxShadow: '0 2px 5px rgba(0,0,0,0.04)'
        }}
        bodyStyle={{ padding: '14px' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div>
            <Text strong style={{ display: 'block', fontSize: '15px', color: '#1e293b' }}>
              {orden.cliente_comercial || orden.cliente_nombre}
            </Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {orden.cliente_comercial ? orden.cliente_nombre : ''}
            </Text>
          </div>
          <Tag color={isCompletado ? 'success' : isCancelado ? 'error' : 'processing'} style={{ margin: 0, fontWeight: 'bold', fontSize: '11px' }}>
            #{orden.numero_orden || orden.id}
          </Tag>
        </div>

        <Space direction="vertical" size={2} style={{ width: '100%', marginBottom: '12px' }}>
          <Text style={{ fontSize: '12px', color: '#64748b' }}>
            <EnvironmentOutlined /> {orden.cliente_direccion || 'Dirección no registrada'}
          </Text>
          {orden.cliente_telefono && (
            <Text style={{ fontSize: '12px', color: '#64748b' }}>
              <PhoneOutlined /> {orden.cliente_telefono}
            </Text>
          )}
          {orden.notas && (
            <div style={{ backgroundColor: '#fffbe6', padding: '6px 8px', borderRadius: '6px', marginTop: '6px' }}>
              <Text style={{ fontSize: '11px', color: '#d48806' }}>
                <strong style={{ marginRight: '4px' }}>Nota:</strong>{orden.notas}
              </Text>
            </div>
          )}
        </Space>

        <Divider style={{ margin: '8px 0' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>TOTAL A COBRAR</Text>
            <Text strong style={{ fontSize: '18px', color: isCompletado ? '#10b981' : '#0f172a' }}>
              ${Number(orden.total).toLocaleString('es-CO')}
            </Text>
          </div>

          <Space wrap>
            <Button
              icon={<EyeOutlined />}
              onClick={() => handleVerDetalles(orden)}
              style={{ borderRadius: '8px', fontWeight: '500' }}
            >
              Ver Ítems
            </Button>

            {!esHistorial && (
              <Button
                type="primary"
                icon={<DollarOutlined />}
                loading={procesandoId === orden.id}
                onClick={() => handleEntregarYCobrar(orden)}
                style={{
                  backgroundColor: '#22BABB',
                  borderRadius: '8px',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 6px rgba(34, 186, 187, 0.2)'
                }}
              >
                Cobrar
              </Button>
            )}
          </Space>
        </div>
      </Card>
    );
  };

  const handleVerDetalles = async (orden) => {
    setOrdenSeleccionada(orden);
    setModalVerOpen(true);
    setLoadingDetalles(true);
    try {
      const token = await getJwtToken();
      const res = await axios.get(`${API_BASE_URL}/v1-ordenes-venta?orden_id=${orden.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetallesOrden(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      message.error("Error al obtener los detalles del pedido.");
    } finally {
      setLoadingDetalles(false);
    }
  };

  return (
    <LayoutHome pageSeleccionada="mi-ruta">
      <div style={{ padding: isMobile ? '10px' : '20px', maxWidth: '600px', margin: '0 auto' }}>

        <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CarOutlined style={{ fontSize: '24px', color: '#22BABB' }} />
          <Title level={4} style={{ margin: 0, color: '#1e293b' }}>Mi Ruta de Reparto</Title>
        </div>

        <Tabs
          defaultActiveKey="1"
          type="card"
          items={[
            {
              key: '1',
              label: (<span><ShoppingCartOutlined /> Por Entregar ({rutaHoy.length})</span>),
              children: loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <Spin size="large" />
                </div>
              ) : (
                <List
                  dataSource={rutaHoy}
                  locale={{ emptyText: 'No tienes pedidos pendientes por entregar.' }}
                  renderItem={o => renderOrdenCard(o, false)}
                />
              )
            },
            {
              key: '2',
              label: (<span><CheckCircleOutlined /> Entregados</span>),
              children: (
                <List
                  dataSource={historial}
                  locale={{ emptyText: 'Aún no has completado entregas hoy.' }}
                  renderItem={o => renderOrdenCard(o, true)}
                />
              )
            }
          ]}
        />
      </div>
      {/* MODAL PARA CONSULTAR LOS PRODUCTOS DEL PEDIDO */}
      <Modal
        title={
          <Space>
            <ShoppingOutlined style={{ color: '#22BABB' }} />
            <span>Pedido #{ordenSeleccionada?.numero_orden || ordenSeleccionada?.id}</span>
          </Space>
        }
        open={modalVerOpen}
        onCancel={() => setModalVerOpen(false)}
        footer={[
          <Button key="cerrar" onClick={() => setModalVerOpen(false)}>
            Cerrar
          </Button>,
          ordenSeleccionada?.estado === 'en_ruta' && (
            <Button
              key="cobrar"
              type="primary"
              icon={<DollarOutlined />}
              style={{ backgroundColor: '#22BABB', borderRadius: '6px' }}
              onClick={() => {
                setModalVerOpen(false);
                handleEntregarYCobrar(ordenSeleccionada);
              }}
            >
              Ir a Cobrar
            </Button>
          )
        ]}
        centered
        width={450}
      >
        {ordenSeleccionada && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
            <div style={{ backgroundColor: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
              <Text strong style={{ display: 'block' }}>{ordenSeleccionada.cliente_comercial || ordenSeleccionada.cliente_nombre}</Text>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block' }}>📍 {ordenSeleccionada.cliente_direccion || 'Sin dirección'}</Text>
              {ordenSeleccionada.notas && (
                <Text style={{ fontSize: '12px', color: '#d48806', marginTop: '4px', display: 'block' }}>
                  <strong>Nota:</strong> {ordenSeleccionada.notas}
                </Text>
              )}
            </div>

            <Table
              dataSource={detallesOrden}
              loading={loadingDetalles}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                { title: 'PRODUCTO', dataIndex: 'producto_nombre' },
                { title: 'CANT.', dataIndex: 'cantidad', align: 'center', render: c => <Text strong>{c}</Text> },
                { title: 'SUBTOTAL', dataIndex: 'subtotal', align: 'right', render: s => `$${Number(s).toLocaleString('es-CO')}` }
              ]}
            />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
              <Text strong>TOTAL:</Text>
              <Text strong style={{ fontSize: '18px', color: '#0f172a' }}>
                ${Number(ordenSeleccionada.total).toLocaleString('es-CO')}
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </LayoutHome>
  );
};

export default MiRuta;